"use client";
import React, { useState } from "react";
import {
  Box, Typography, Button, MenuItem, CircularProgress, 
  Collapse, Stack, Paper, Chip
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  assignAssessmentFormToClient,
  getAllAssessmentForms,
  getAssignedFormsByClient,
} from "../../../../../api/services/Assessment/AssignAssessment";
import { QK } from "../../../../../base/qk";
import CustomInput from "../../../../CustomForm/CustomInput";
import { useParams, useNavigate } from "react-router-dom";

const AssignAssessmentForm = () => {
  const [selectedForm, setSelectedForm] = useState("");
  const [open, setOpen] = useState(false);
  const { clientID, assignedCarePlanID } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: availableForms, isLoading: formsLoading } = useQuery(
    [QK.assignedAssessments, "active"],
    () => getAllAssessmentForms("active"),
    { 
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const { data: assignedForms } = useQuery(
    [QK.assignedForms, clientID, assignedCarePlanID],
    () => getAssignedFormsByClient(clientID, assignedCarePlanID),
    { 
      enabled: !!clientID && !!assignedCarePlanID,
      staleTime: 5 * 60 * 1000
    }
  );

  const assignedFormIds = new Set(assignedForms?.map(af => af.form) || []);
  const unassignedForms = availableForms?.filter(
    form => !assignedFormIds.has(form.id)
  ) || [];

  const mutation = useMutation(assignAssessmentFormToClient, {
    onSuccess: () => {
      enqueueSnackbar("Assigned successfully!", { variant: "success" });
      queryClient.invalidateQueries([QK.assignedForms, clientID, assignedCarePlanID]);
      setSelectedForm("");
    },
    onError: (error) => {
      const errorMsg = error?.response?.data?.detail || "Failed to assign form";
      enqueueSnackbar(errorMsg, { variant: "error" });
    },
  });

  const handleAssign = () => {
    if (!selectedForm) {
      enqueueSnackbar("Please select a form first", { variant: "warning" });
      return;
    }
    mutation.mutate({ 
      client: clientID, 
      form: selectedForm,
      care_plan: assignedCarePlanID
    });
  };

  const handleGoToAssessment = (formID, carePlanID) => {
    navigate(`/active-assessments/${clientID}?form=${formID}&careplan=${carePlanID}`);
  };

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      <Button
          fullWidth
          onClick={() => setOpen((prev) => !prev)}
          variant="contained"
          sx={{
            justifyContent: "space-between",
            py: 2,
            px: 3,
            borderRadius: "12px",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "1rem",
            background: open
              ? "linear-gradient(135deg, #065f46, #047857)"
              : "linear-gradient(135deg, #10b981, #059669)",
            color: "white",
            boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)",
            "&:hover": {
              background: "linear-gradient(135deg, #059669, #047857)",
              boxShadow: "0 6px 15px rgba(16, 185, 129, 0.3)",
            },
            "& .MuiTypography-root": {
              color: "white"
            }
          }}
        >
        <Stack direction="row" spacing={2} alignItems="center">
          <AssignmentIcon sx={{ fontSize: 22 }} />
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            Assign Assessment
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip 
            label={`${assignedForms?.length || 0} Assigned`} 
            size="small"
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              fontWeight: 600,
              fontSize: '0.7rem'
            }} 
          />
          {open ? (
            <KeyboardArrowUpIcon sx={{ fontSize: 24 }} />
          ) : (
            <KeyboardArrowDownIcon sx={{ fontSize: 24 }} />
          )}
        </Stack>
      </Button>

      <Collapse in={open}>
        <Box
          sx={{
            mt: 2,
            borderRadius: "16px",
            p: 3,
            bgcolor: "#f0fdf4",
            border: "1px solid #dcfce7",
          }}
        >
          {formsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={30} color="success" />
            </Box>
          ) : (
            <>
              <CustomInput
                select
                label="Available Assessments"
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                fullWidth
                size="small"
                sx={{ 
                  mb: 2,
                  "& .MuiInputBase-root": { 
                    fontSize: '0.85rem',
                    bgcolor: 'white',
                    borderRadius: '8px'
                  }
                }}
              >
                {unassignedForms && unassignedForms.length > 0 ? (
                  unassignedForms.map((form) => (
                    <MenuItem key={form.id} value={form.id} sx={{ fontSize: '0.85rem' }}>
                      {form.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ fontSize: '0.85rem' }}>
                    {availableForms?.length === 0 ? "No forms available" : "All forms are already assigned"}
                  </MenuItem>
                )}
              </CustomInput>

              <Button
                variant="contained"
                fullWidth
                disableElevation
                onClick={handleAssign}
                disabled={mutation.isLoading || !selectedForm || !assignedCarePlanID || unassignedForms.length === 0}
                sx={{ 
                  bgcolor: "#0f172a", 
                  borderRadius: "8px", 
                  textTransform: "none", 
                  fontWeight: 700, 
                  fontSize: '0.85rem', 
                  py: 1.2,
                  "&:hover": { bgcolor: "#1e293b" }
                }}
              >
                {mutation.isLoading ? "Assigning..." : "Confirm Assignment"}
              </Button>

              {assignedForms?.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" sx={{ 
                    fontWeight: 800, 
                    color: "#64748b", 
                    letterSpacing: 0.5, 
                    display: 'block', 
                    mb: 1.5, 
                    fontSize: '0.7rem' 
                  }}>
                    ASSIGNED HISTORY ({assignedForms.length})
                  </Typography>

                  <Box sx={{ 
                    maxHeight: 200, 
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': {
                      width: '3px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#e2e8f0',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#94a3b8',
                      borderRadius: '4px',
                    },
                  }}>
                    <Stack spacing={0.5}>
                      {assignedForms.map((af) => (
                        <Paper
                          key={af.id}
                          elevation={0}
                          onClick={() => handleGoToAssessment(af.form, af.care_plan)}
                          sx={{
                            p: 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderRadius: "8px",
                            bgcolor: "white",
                            border: "1px solid #e2e8f0",
                            cursor: "pointer",
                            transition: "0.2s",
                            "&:hover": { 
                              bgcolor: "#f8fafc", 
                              borderColor: "#10b981",
                              transform: "translateX(3px)"
                            }
                          }}
                        >
                          <Typography variant="caption" sx={{ 
                            fontWeight: 700, 
                            color: "#1e293b", 
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '140px'
                          }}>
                            {af.form_title}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: "#94a3b8", 
                            fontSize: '0.65rem',
                            flexShrink: 0,
                            ml: 1
                          }}>
                            {new Date(af.assigned_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric'
                            })}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AssignAssessmentForm;